import { useState, useEffect, useMemo, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useBrief } from "@/context/BriefContext";
import { submitBrief } from "@/lib/api";
import { track, getUTM } from "@/lib/analytics";
import { Check, X } from "lucide-react";
import { amp } from "@/lib/amp";

/* Canonical selector set (generic launch points). Values map to backend tags. */
const SELECTOR_OPTIONS = [
  { value: "general", label: "General inquiry" },
  { value: "founder-tech-clarity-sprint", label: "Founder Tech Clarity Sprint" },
  { value: "90-day-business-modernization-accelerator", label: "90-Day Business Modernization Accelerator" },
  { value: "fractional-strategy-and-go-to-market", label: "Fractional Strategy & GTM" },
  { value: "fractional-cio", label: "Fractional CIO" },
  { value: "fractional-cto-and-enterprise-product-partnership", label: "Fractional CTO & Enterprise Product Partnership" },
  { value: "institutional", label: "Institutional" },
  { value: "executive-production", label: "Executive Production" },
  { value: "nonprofit-operations-modernization", label: "Nonprofit / Operations Modernization" },
];

/* Read-only context label for any offer slug a specific CTA may pass. */
const OFFER_LABELS = {
  general: "General inquiry",
  "founder-tech-clarity-sprint": "Founder Tech Clarity Sprint",
  "90-day-business-modernization-accelerator": "90-Day Business Modernization Accelerator",
  "fractional-strategy-and-go-to-market": "Fractional Strategy & GTM",
  "fractional-cio": "Fractional CIO",
  "fractional-cto-and-enterprise-product-partnership": "Fractional CTO & Enterprise Product Partnership",
  institutional: "Institutional",
  "executive-production": "Executive Production",
  "nonprofit-operations-modernization": "Nonprofit / Operations Modernization",
  // Enterprise ladder
  "enterprise-ai-readiness-governance-sprint": "Enterprise AI Readiness & Governance Sprint",
  "enterprise-fractional-cio": "Fractional CIO",
  "enterprise-fractional-cto": "Fractional CTO & Enterprise Product Partnership",
  "enterprise-institutional": "Institutional",
  // Entrepreneur ladder
  "concept-to-customer-clarity-sprint": "Concept-to-Customer Clarity Sprint",
  "concept-to-pilot-business-model": "Concept-to-Pilot Business Model",
  "entrepreneur-founder-sprint-graduate": "Founder Tech Clarity Sprint",
  "entrepreneur-fractional-strategy": "Fractional Strategy & GTM",
  // Nonprofit ladder
  "nonprofit-tech-ai-readiness-sprint": "Nonprofit Tech & AI Readiness Sprint",
  "nonprofit-90-day-operations-modernization": "90-Day Operations Modernization",
  "nonprofit-fractional-cio": "Fractional CIO",
  "nonprofit-salesforce-platform-implementation": "Salesforce & Platform Implementation",
  "nonprofit-institutional": "Institutional",
  // Public sector
  "public-sector": "Public Sector Advisory",
};

const prettify = (slug) =>
  slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const EMPTY = { name: "", email: "", role: "", prompt: "", talkFirst: false };

export function BriefModal() {
  const { open, offer, setOpen } = useBrief();
  const isSpecific = !!offer && offer !== "general";

  const [selected, setSelected] = useState("general");
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errors, setErrors] = useState({});
  const nameRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStatus("idle");
      setErrors({});
      setForm(EMPTY);
      setSelected("general");
    }
  }, [open]);

  const effectiveOffer = isSpecific ? offer : selected;
  const contextLabel = useMemo(
    () => OFFER_LABELS[effectiveOffer] || prettify(effectiveOffer),
    [effectiveOffer]
  );

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please add your name.";
    if (!form.email.trim()) e.email = "Please add your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "That email does not look right.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    try {
      await submitBrief({
        name: form.name,
        email: form.email,
        role: form.role,
        prompt: form.prompt,
        offer: effectiveOffer,
        offer_label: contextLabel,
        talk_first: form.talkFirst,
        utm: getUTM(),
      });
      track("brief_request", { offer: effectiveOffer, talk_first: form.talkFirst });
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-cv-brief-overlay
          className="fixed inset-0 z-[200] bg-[#221E1B]/55 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 duration-150"
        />
        <DialogPrimitive.Content
          data-testid="brief-request-modal"
          className="exec-scope fixed left-1/2 top-1/2 z-[200] w-[calc(100%-2rem)] max-w-[500px] -translate-x-1/2 -translate-y-1/2 max-h-[90vh] overflow-y-auto rounded-[2px] border border-[color:var(--rule)] bg-[color:var(--paper)] p-8 sm:p-10 shadow-[0_8px_40px_rgba(34,30,27,0.18)] focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 motion-safe:data-[state=open]:zoom-in-95 motion-safe:data-[state=closed]:zoom-out-95 duration-200"
          onOpenAutoFocus={(e) => {
            if (isSpecific && nameRef.current) {
              e.preventDefault();
              nameRef.current.focus();
            }
          }}
        >
          <DialogPrimitive.Close
            data-testid="brief-request-x"
            aria-label="Close"
            className="absolute right-3 top-3 w-11 h-11 flex items-center justify-center text-[color:var(--grey)] hover:text-[color:var(--ink)] transition-colors"
          >
            <X size={20} />
          </DialogPrimitive.Close>

          {status === "success" ? (
            <div data-testid="brief-request-success" className="py-2">
              <span className="w-10 h-10 rounded-full border border-[color:var(--copper)] flex items-center justify-center">
                <Check size={18} className="text-[color:var(--copper-deep)]" />
              </span>
              <DialogPrimitive.Title className="font-light text-[clamp(24px,3vw,30px)] leading-[1.18] mt-5">
                The brief is on its way.
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-4">
                We’ve got it. Your brief — the offering, the deliverable, and the timeline — is on its way, within
                one business day. The next move is yours.
                {form.talkFirst && " We’ll include a few times."}
              </DialogPrimitive.Description>
              <button
                data-testid="brief-request-close-button"
                onClick={() => setOpen(false)}
                className="exec-btn exec-btn-secondary mt-7"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Context line / selector */}
              {isSpecific ? (
                <p data-testid="brief-context-label" className="text-[11px] uppercase tracking-[0.2em] font-normal text-[color:var(--copper-deep)]">
                  Brief: {amp(contextLabel)}
                </p>
              ) : (
                <div>
                  <label htmlFor="brief-offer-select" className="block text-[11px] uppercase tracking-[0.2em] font-normal text-[color:var(--copper-deep)]">
                    Brief
                  </label>
                  <select
                    id="brief-offer-select"
                    data-testid="brief-offer-select"
                    value={selected}
                    onChange={(e) => setSelected(e.target.value)}
                    className="exec-underline mt-1 cursor-pointer"
                  >
                    {SELECTOR_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <DialogPrimitive.Title className="font-light text-[clamp(24px,3vw,30px)] leading-[1.18] mt-5">
                Request the brief
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="exec-serif text-[17px] leading-[1.55] text-[color:var(--lead-ink)] mt-3">
                Tell us where you are. We’ll send the relevant brief — the offering, the deliverable, the
                timeline — within one business day. The next move is yours.
              </DialogPrimitive.Description>

              <form onSubmit={handleSubmit} className="mt-7 space-y-6" noValidate>
                <Field id="brief-name" label="Name" error={errors.name}>
                  <input
                    ref={nameRef}
                    id="brief-name"
                    data-testid="brief-request-name-input"
                    className="exec-underline"
                    autoComplete="name"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "brief-name-err" : undefined}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </Field>
                <Field id="brief-email" label="Email" error={errors.email}>
                  <input
                    id="brief-email"
                    data-testid="brief-request-email-input"
                    type="email"
                    className="exec-underline"
                    autoComplete="email"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "brief-email-err" : undefined}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </Field>
                <Field id="brief-role" label="Role" optional>
                  <input
                    id="brief-role"
                    data-testid="brief-request-role-input"
                    className="exec-underline"
                    placeholder="Founder, CIO, advisor"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                  />
                </Field>
                <Field id="brief-prompt" label="What is the decision in front of you?" optional>
                  <textarea
                    id="brief-prompt"
                    data-testid="brief-request-prompt-input"
                    rows={3}
                    className="exec-underline resize-none"
                    value={form.prompt}
                    onChange={(e) => setForm({ ...form, prompt: e.target.value })}
                  />
                </Field>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="brief-request-talk-first"
                    checked={form.talkFirst}
                    onChange={(e) => setForm({ ...form, talkFirst: e.target.checked })}
                    className="mt-0.5 w-[18px] h-[18px] cursor-pointer"
                    style={{ accentColor: "#a0644b" }}
                  />
                  <span className="exec-serif text-[16px] text-[color:var(--lead-ink)]">
                    I’d rather talk first — send times with the brief.
                  </span>
                </label>

                {status === "error" && (
                  <p data-testid="brief-request-error" role="alert" className="exec-serif text-[16px] text-[color:var(--ink)] border-l-2 border-[color:var(--copper)] pl-3">
                    That didn’t go through. Try again, or email us at{" "}
                    <a href="mailto:info@coppervine.io" className="exec-link inline normal-case">info@coppervine.io</a>.
                  </p>
                )}

                <div className="flex justify-end pt-1">
                  <button
                    type="submit"
                    data-testid="brief-request-submit-button"
                    disabled={status === "submitting"}
                    className="exec-btn exec-btn-primary w-full sm:w-auto disabled:opacity-60"
                  >
                    {status === "submitting" ? "Sending\u2026" : "Send it"}
                  </button>
                </div>
              </form>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function Field({ id, label, optional, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[11px] uppercase tracking-[0.16em] font-normal text-[color:var(--ink)]">
        {label}
        {optional && <span className="text-[color:var(--grey)] font-normal normal-case tracking-normal"> (optional)</span>}
      </label>
      <div className="mt-1">{children}</div>
      {error && (
        <p id={`${id}-err`} className="exec-serif text-[14px] text-[color:var(--copper-deep)] mt-1.5">{error}</p>
      )}
    </div>
  );
}
